import React from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import beGetClientSmsText from 'actions/sms/beGetClientSmsText.action';
import Button from 'components/form/Button.component';
import Badge from 'components/generic/Badge.component';
import Icon from 'components/generic/Icon.component';
import Space from 'components/generic/Space.component';
import SmsBundle from 'components/price/SmsBundle.component';
import LINK from 'constants/link.const';
import PLAN_NAME from 'constants/plan.const';
import useAef from 'hooks/useAef.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import {
  selectCurrentSalon,
  selectCurrentSalonAutoTopUpSms,
  selectCurrentSalonBilling,
  selectCurrentSalonDigitalCardAddOn,
  selectCurrentSalonPricingPlan,
} from 'selectors/currentSalon.selectors';
import { selectBillingCardData } from 'selectors/settings.selectors';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { formatDayToYearOrdinal } from 'utils/date/format.util';
import { isEnvMobile } from 'utils/env/determine.util';
import linkTo from 'utils/links/linkTo.util';
import isEmptyString from 'utils/predicates/string/isEmptyString.util';

import CN from './SalonSubscription.module.scss';

const NavLinkContent = () => {
  const { t } = useTranslation();

  return (
    <div className={CN.buttonInner}>
      <Icon variant="pencil" selected width="md" />
      <div className={CN.buttonInnerText}>{t('common.buttons.manage')}</div>
    </div>
  );
};

const SalonSubscription = ({
  isDacEnabled,
  pricingPlan,
  autoTopUp,
  currentSalon,
  stripeCardData,
  onButtonClick,
  hasBilling,
  beGetClientSmsText,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const isMobile = isEnvMobile();
  const salonId = useCurrentSalonId();

  const resultSmsTextRead = useAef(
    async () => (await beGetClientSmsText({ salonId })).json,
    [beGetClientSmsText, salonId],
  );

  const {
    enable_sms_after_reminder: afterReminder,
    enable_sms_bday_clients_text: bdayClients,
    enable_sms_lapsed_7_weeks_text: lapsed7,
    enable_sms_lapsed_12_weeks_text: lapsed12,
    enable_sms_lapsed_24_weeks_text: lapsed24,
    enable_sms_new_client_thank_you_text: newClientThanks,
    enable_sms_rebook_prompt_text: rebookPrompt,
    enable_sms_thank_you_text: thankYou,
  } = resultSmsTextRead.value ?? {};

  const autoTopUpActive =
    autoTopUp &&
    (afterReminder || bdayClients || lapsed7 || lapsed12 || lapsed24 || newClientThanks || rebookPrompt || thankYou);

  const linkMarketingNotification = linkTo({ pattern: LINK.reminders, params: { salonId }, fault: '#' });
  const linkMarketingAutomatedTexts = linkTo({ pattern: LINK.clientConnect, params: { salonId }, fault: '#' });

  const {
    is_cancelled: isCancelled,
    subscription_id: subscriptionId,
    subscription_start_date: subscriptionStartDate,
    subscription_cancelled_date: subscriptionCancelledDate,
  } = stripeCardData ?? {};

  return (
    <div
      data-bem="SalonSubscription"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      <div className={CN.container}>
        <div className={CN.flex}>
          <div className={CN.pageTitle}>
            <div className={CN.flexRow}>
              <div className={CN.pageIcon}>
                <Icon variant="arrowPath" width="xl" variantInvert />
              </div>
              {t('staffAndBusiness.subAndBilling.subscription.title')}
            </div>

            <div class={CN.buttonContainer}>
              <Button variant="secondary" onClick={onButtonClick}>
                {hasBilling ? t('common.buttons.editUpp') : t('common.buttons.addUpp')}{' '}
                {t('staffAndBusiness.subAndBilling.subscription.billingDetailsButton')}
              </Button>
            </div>
          </div>

          <div
            className={cn({
              [CN.grid]: true,
              [CN.active]: subscriptionId && !isCancelled,
              [CN.cancelled]: subscriptionId && isCancelled,
              [CN.trial]: !subscriptionId,
            })}
          >
            <div className={CN.column}>
              <div className={CN.columnTitle}>{t('staffAndBusiness.subAndBilling.subscription.accountStatus')}</div>
              <div className={CN.badgeContainer}>
                {subscriptionId ? (
                  isCancelled ? (
                    <Badge variant="alert">
                      <Icon variant="xCircleSolid" width={isMobile ? 'sm' : 'md'} />
                      <div className={CN.badgeFont}>
                        {t('staffAndBusiness.subAndBilling.subscription.badgeCancelled')}
                      </div>
                    </Badge>
                  ) : (
                    <Badge variant="success">
                      <Icon variant="checkCircleSolid" width={isMobile ? 'sm' : 'md'} />
                      <div className={CN.badgeFont}>{t('staffAndBusiness.subAndBilling.subscription.badgeActive')}</div>
                    </Badge>
                  )
                ) : (
                  <Badge variant="draft">
                    <Icon variant="checkCircleSolid" width={isMobile ? 'sm' : 'md'} />
                    <div className={CN.badgeFont}>
                      {t('staffAndBusiness.subAndBilling.subscription.badgeFreeTrial')}
                    </div>
                  </Badge>
                )}
              </div>
            </div>
            <div className={CN.column}>
              <div className={CN.columnTitle}>{t('staffAndBusiness.subAndBilling.subscription.planType')}</div>
              <div className={CN.columnValue}>
                {isEmptyString(currentSalon.pricing_plan) ? 'None' : currentSalon.pricing_plan}
                {pricingPlan === PLAN_NAME.essential && isDacEnabled && (
                  <Space>{t('staffAndBusiness.subAndBilling.subscription.digitalCards')}</Space>
                )}
              </div>
            </div>
            <div className={CN.column}>
              <div className={CN.columnTitle}>{t('staffAndBusiness.subAndBilling.subscription.signedUp')}</div>
              <div className={CN.columnValue}>
                {/* TODO: date localisation: change with formatDateMid when ready */}
                {subscriptionId ? formatDayToYearOrdinal(subscriptionStartDate) : '---'}
              </div>
            </div>
            {isCancelled && (
              <div className={CN.row}>
                <div className={CN.column}>
                  <div className={CN.columnTitle}>{t('staffAndBusiness.subAndBilling.subscription.cancelledOn')}</div>
                  <div className={CN.columnValue}>
                    {/* TODO: date localisation: change with formatDateMid when ready */}
                    <div className={CN.alert}>{formatDayToYearOrdinal(subscriptionCancelledDate)}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={CN.subTitle}>{t('staffAndBusiness.subAndBilling.subscription.sectionAddonsTitle')}</div>
          <div class={CN.addOnGrid}>
            <div
              className={cn({
                [CN.box]: true,
                [CN.disabled]: !isDacEnabled,
              })}
            >
              <div className={CN.badgeContainer}>
                <Badge variant={isDacEnabled ? 'success' : 'alert'}>
                  <Icon variant="checkCircleSolid" width={isMobile ? 'sm' : 'md'} />
                  <div className={CN.badgeFont}>
                    {isDacEnabled ? t('common.enabled') : t('common.disabled')}
                    <Space>
                      {pricingPlan === PLAN_NAME.advanced || pricingPlan === PLAN_NAME.supercharged
                        ? t('staffAndBusiness.subAndBilling.subscription.includedInPlan')
                        : t('staffAndBusiness.subAndBilling.subscription.byAccountOwner')}
                    </Space>
                  </div>
                </Badge>
              </div>
              <div className={CN.comboTitle}>{t('staffAndBusiness.subAndBilling.subscription.box1Title')}</div>
              <div className={CN.text}>{t('staffAndBusiness.subAndBilling.subscription.box1Description')}</div>
              <NavLink to={linkMarketingNotification}>
                <NavLinkContent />
              </NavLink>
            </div>
            <div
              className={cn({
                [CN.box]: true,
                [CN.disabled]: !autoTopUpActive,
              })}
            >
              <div className={CN.badgeContainer}>
                <Badge variant={autoTopUpActive ? 'success' : 'alert'}>
                  <Icon
                    variant={autoTopUpActive ? 'checkCircleSolid' : 'xCircleSolid'}
                    width={isMobile ? 'sm' : 'md'}
                  />
                  <div className={CN.badgeFont}>
                    {autoTopUpActive ? t('common.enabled') : t('common.disabled')}
                    <Space>{t('staffAndBusiness.subAndBilling.subscription.byAccountOwner')}</Space>
                  </div>
                </Badge>
              </div>
              <div className={CN.comboTitle}>{t('staffAndBusiness.subAndBilling.subscription.box2Title')}</div>
              <div className={CN.text}>
                {t('staffAndBusiness.subAndBilling.subscription.box2DescriptionPart1')}{' '}
                <SmsBundle>{t('staffAndBusiness.subAndBilling.subscription.box2DescriptionPart2')}</SmsBundle>
              </div>
              <NavLink to={linkMarketingAutomatedTexts}>
                <NavLinkContent />
              </NavLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

//
const SalonSubscriptionHoc = connect(
  (state) => ({
    currentSalon: selectCurrentSalon(state),
    stripeCardData: selectBillingCardData(state),
    pricingPlan: selectCurrentSalonPricingPlan(state),
    isDacEnabled: selectCurrentSalonDigitalCardAddOn(state),
    autoTopUp: selectCurrentSalonAutoTopUpSms(state),
    hasBilling: selectCurrentSalonBilling(state),
  }),
  mapActionsToDispatchToProps({
    beGetClientSmsText,
  }),
)(SalonSubscription);

export default SalonSubscriptionHoc;
